import { useEffect, useState } from 'react';

import { _notNil } from '@/littledash';
import type { Animal, AnimalApiId } from '@/model/Animal.model';
import { ApiService, ResponseType } from '@/support/ApiService';

import { EMPTY_CASCADE_DELETE_ITEM_COUNTS, type CascadeDeleteItemCounts } from './ConfirmDelete.model';

/** Matches the server-side maximum for cascade delete item count requests. */
export const CASCADE_DELETE_ITEM_COUNT_ANIMAL_LIMIT = 500;

export type CascadeDeleteItemCountStatus = 'idle' | 'loading' | 'ready' | 'unavailable' | 'too_many';

export interface CascadeDeleteItemCountState {
  status: CascadeDeleteItemCountStatus;
  counts: CascadeDeleteItemCounts;
}

interface LoadedItemCounts {
  animalApiIdsKey: string;
  counts: CascadeDeleteItemCounts;
}

export const animalCascadeDeleteApiIds = (animals: Array<Animal>): Array<AnimalApiId> =>
  animals.reduce<Array<AnimalApiId>>((apiIds, { api_id: apiId }) => {
    if (_notNil(apiId)) {
      apiIds.push(apiId);
    }
    return apiIds;
  }, []);

const resolveCascadeDeleteItemCountStatus = (
  enabled: boolean,
  animalApiIds: Array<AnimalApiId>,
  animalApiIdsKey: string,
  loadedItemCounts: LoadedItemCounts | undefined,
  failedAnimalApiIdsKey: string | undefined
): CascadeDeleteItemCountStatus => {
  if (!enabled || animalApiIds.length === 0) {
    return 'idle';
  }
  if (animalApiIds.length > CASCADE_DELETE_ITEM_COUNT_ANIMAL_LIMIT) {
    return 'too_many';
  }
  if (failedAnimalApiIdsKey === animalApiIdsKey) {
    return 'unavailable';
  }
  if (loadedItemCounts?.animalApiIdsKey === animalApiIdsKey) {
    return 'ready';
  }
  return 'loading';
};

/**
 * Aggregate counts of the child records a cascade delete would remove for `animalApiIds`. Exposes an
 * explicit status so the modal can distinguish loading, unavailable, and confirmed zero counts.
 */
export const useAnimalCascadeDeleteItemCounts = (
  animalApiIds: Array<AnimalApiId>,
  enabled: boolean
): CascadeDeleteItemCountState => {
  const [loadedItemCounts, setLoadedItemCounts] = useState<LoadedItemCounts>();
  const [failedAnimalApiIdsKey, setFailedAnimalApiIdsKey] = useState<string>();
  const animalApiIdsKey = [...animalApiIds].sort().join(',');
  const shouldFetch =
    enabled && animalApiIds.length > 0 && animalApiIds.length <= CASCADE_DELETE_ITEM_COUNT_ANIMAL_LIMIT;

  useEffect(() => {
    if (!shouldFetch) {
      return;
    }

    const controller = new AbortController();
    ApiService.call({
      endpoint: 'POST /api/v1/animals/cascade-delete-item-count',
      body: { animal_api_ids: animalApiIds },
      signal: controller.signal,
      options: { onError: { toast: false, throw: false, capture: false, slug: 'animal-cascade-delete-item-count' } },
    })
      .then((response) => {
        if (controller.signal.aborted) {
          return;
        }
        if (response.type === ResponseType.Success && _notNil(response.body)) {
          setLoadedItemCounts({ animalApiIdsKey, counts: response.body });
          setFailedAnimalApiIdsKey(undefined);
          return;
        }
        setFailedAnimalApiIdsKey(animalApiIdsKey);
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setFailedAnimalApiIdsKey(animalApiIdsKey);
        }
      });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps, react-x/exhaustive-deps
  }, [shouldFetch, animalApiIdsKey]);

  const status = resolveCascadeDeleteItemCountStatus(
    enabled,
    animalApiIds,
    animalApiIdsKey,
    loadedItemCounts,
    failedAnimalApiIdsKey
  );

  return {
    status,
    counts:
      status === 'ready' && _notNil(loadedItemCounts) ? loadedItemCounts.counts : EMPTY_CASCADE_DELETE_ITEM_COUNTS,
  };
};
